import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import ScoreBadge from '../components/ScoreBadge';
import Avatar from '../components/Avatar';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { SkeletonTable, SkeletonHeader, SkeletonBreadcrumb } from '../components/Skeleton';

export default function Clinics() {
  const { profile } = useAuth();
  const [search, setSearch] = useState('');
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    api.clinics.list()
      .then(setClinics)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [profile?.id]);

  const filteredClinics = clinics.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div>
      <SkeletonBreadcrumb />
      <SkeletonHeader />
      <SkeletonTable rows={4} />
    </div>
  );

  return (
    <div>
      <Breadcrumb items={[
        { label: 'Home', to: '/' },
        { label: 'Clinics' },
      ]} />

      <h1 className="text-2xl md:text-[32px] font-semibold text-text mb-6">Clinics</h1>

      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
          <h3 className="text-xl font-bold text-text">All Clinics</h3>
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-200 rounded-lg px-4 py-2 text-sm w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
          />
        </div>

        <table className="w-full hidden sm:table">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 text-sm font-semibold text-gray-700">Name</th>
              <th className="text-center py-3 text-sm font-semibold text-gray-700 w-48">Average CScore</th>
            </tr>
          </thead>
          <tbody>
            {filteredClinics.map((clinic) => (
              <tr key={clinic.id} className="border-b border-gray-50 last:border-0">
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    <Avatar src={clinic.image_url} name={clinic.name} size="md" />
                    <div>
                      <Link to={`/clinics/${clinic.id}`} className="text-primary font-medium hover:underline text-sm">{clinic.name}</Link>
                      <p className="text-xs text-gray-500 mt-0.5">{clinic.clinician_count} Clinician{clinic.clinician_count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 text-center"><ScoreBadge score={clinic.avg_cscore} /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="sm:hidden">
          {filteredClinics.map((clinic, idx) => (
            <div key={clinic.id} className={`flex items-center justify-between py-3 ${idx !== filteredClinics.length - 1 ? 'border-b border-gray-100' : ''}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Avatar src={clinic.image_url} name={clinic.name} size="md" />
                <div className="min-w-0">
                  <Link to={`/clinics/${clinic.id}`} className="text-primary font-medium hover:underline text-sm truncate block">{clinic.name}</Link>
                  <p className="text-xs text-gray-500">{clinic.clinician_count} Clinician{clinic.clinician_count !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <ScoreBadge score={clinic.avg_cscore} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
